import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Search, ArrowUpDown } from 'lucide-react';
import { expenseCategoryService } from '../services/expenseCategoryService';

type CategoryRow = {
  localId: string;
  id?: string;
  code: string;
  label: string;
  keywords: string;
  sort_order?: number;
  is_active?: number | boolean;
};

const makeLocalId = () => `expense-category-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function ExpenseCategories() {
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await expenseCategoryService.getCategories(true);
      setRows((response.data || response || []).map((item: any) => ({
        localId: String(item.id || makeLocalId()),
        id: String(item.id),
        code: item.code,
        label: item.label,
        keywords: item.keywords || '',
        sort_order: Number(item.sort_order || 0),
        is_active: Number(item.is_active || 0) === 1,
      })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveRow = async (row: CategoryRow) => {
    const payload = {
      code: row.code,
      label: row.label,
      keywords: row.keywords,
      sortOrder: row.sort_order || 0,
      isActive: Boolean(row.is_active),
    };
    if (row.id) {
      await expenseCategoryService.updateCategory(row.id, payload);
    } else {
      await expenseCategoryService.createCategory(payload);
    }
    await loadData();
  };

  const deleteRow = async (row: CategoryRow) => {
    if (!row.id) {
      setRows((current) => current.filter((item) => item !== row));
      return;
    }
    await expenseCategoryService.deleteCategory(row.id);
    await loadData();
  };

  const sortRows = () => {
    setRows((current) => [...current].sort((a, b) => {
      const sortA = Number(a.sort_order || 0);
      const sortB = Number(b.sort_order || 0);
      if (sortA !== sortB) return sortA - sortB;
      return a.label.localeCompare(b.label, 'tr');
    }));
  };

  const filteredRows = useMemo(() => rows.filter((row) => {
    const normalized = `${row.code} ${row.label} ${row.keywords}`.toLocaleLowerCase('tr-TR');
    return normalized.includes(search.toLocaleLowerCase('tr-TR'));
  }), [rows, search]);

  const bulkSetActive = (isActive: boolean) => {
    if (selectedIds.length === 0) return;
    setRows((current) => current.map((row) => selectedIds.includes(String(row.id)) ? { ...row, is_active: isActive } : row));
  };

  const bulkSave = async () => {
    if (selectedIds.length === 0) return;
    const targets = rows.filter((row) => selectedIds.includes(String(row.id)));
    for (const row of targets) {
      if (row.id) {
        await expenseCategoryService.updateCategory(row.id, {
          code: row.code,
          label: row.label,
          keywords: row.keywords,
          sortOrder: row.sort_order || 0,
          isActive: Boolean(row.is_active),
        });
      }
    }
    setSelectedIds([]);
    await loadData();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Masraf Kategorileri</h1>
          <p className="text-sm text-gray-600 mt-1">Mobil masraf girişinde kullanılacak kategorileri tablo olarak yönetin</p>
        </div>
        <button onClick={() => setRows((current) => [...current, { localId: makeLocalId(), code: '', label: '', keywords: '', sort_order: current.length + 1, is_active: true }])} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Yeni Kategori
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kod, kategori adı veya anahtar kelime ara"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2"
          />
        </div>
        <button onClick={sortRows} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <ArrowUpDown className="w-4 h-4" /> Toplu Sırala
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <button onClick={() => bulkSetActive(true)} disabled={selectedIds.length === 0} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">Seçilileri Aktif Yap</button>
        <button onClick={() => bulkSetActive(false)} disabled={selectedIds.length === 0} className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium disabled:opacity-50">Seçilileri Pasif Yap</button>
        <button onClick={bulkSave} disabled={selectedIds.length === 0} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50">Seçilileri Kaydet</button>
        <span className="text-sm text-gray-500">Seçili kayıt: {selectedIds.length}</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? <div className="p-6 text-sm text-gray-500">Yükleniyor...</div> : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <input
                    type="checkbox"
                    checked={filteredRows.length > 0 && selectedIds.length === filteredRows.filter((row) => row.id).length}
                    onChange={(e) => setSelectedIds(e.target.checked ? filteredRows.map((row) => String(row.id)).filter(Boolean) : [])}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kod</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori Adı</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anahtar Kelimeler</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sıra</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktif</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRows.map((row) => (
                <tr key={row.localId}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.includes(String(row.id))} onChange={(e) => setSelectedIds((current) => e.target.checked ? [...current, String(row.id)] : current.filter((id) => id !== String(row.id)))} /></td>
                  <td className="px-4 py-3"><input value={row.code} onChange={(e) => setRows((current) => current.map((item) => item.localId === row.localId ? { ...item, code: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                  <td className="px-4 py-3"><input value={row.label} onChange={(e) => setRows((current) => current.map((item) => item.localId === row.localId ? { ...item, label: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                  <td className="px-4 py-3"><input value={row.keywords} onChange={(e) => setRows((current) => current.map((item) => item.localId === row.localId ? { ...item, keywords: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                  <td className="px-4 py-3"><input type="number" value={row.sort_order || 0} onChange={(e) => setRows((current) => current.map((item) => item.localId === row.localId ? { ...item, sort_order: Number(e.target.value) } : item))} className="w-24 border rounded-lg px-3 py-2" /></td>
                  <td className="px-4 py-3"><input type="checkbox" checked={Boolean(row.is_active)} onChange={(e) => setRows((current) => current.map((item) => item.localId === row.localId ? { ...item, is_active: e.target.checked } : item))} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => saveRow(row)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-white text-sm"><Save className="w-4 h-4" /> Kaydet</button>
                      <button onClick={() => deleteRow(row)} className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-white text-sm"><Trash2 className="w-4 h-4" /> Sil</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
